import { useRef, useState } from 'react';
import { useInterval } from 'react-use';
import { convertSecondsToTime } from 'utils/timeHelper';
import { differenceInSeconds, subSeconds } from 'date-fns';

import { useCreateTracker } from './useCreateTracker';
import { deleteActionTimeTracker } from './api';

import type { RefObject } from 'react';

interface UseTrackerResult {
  time: string;
  secondsRef: RefObject<number>;
  start: () => void;
  stop: () => void;
  createTrackerLoading: boolean;
  trackerGuid: string | null;
  deleteTracker: () => void;
  resume: () => void;
}

export const useTracker = (): UseTrackerResult => {
  const date = useRef<Date>(new Date());
  const secondsRef = useRef<number>(0);
  const [time, setTime] = useState<string>(convertSecondsToTime(0));

  const [delay, setDelay] = useState<number | null>(null);
  const [trackerGuid, setTrackerGuid] = useState<string | null>(null);

  const updateTime = () => {
    const seconds = differenceInSeconds(new Date(), date.current);
    secondsRef.current = seconds;
    setTime(convertSecondsToTime(seconds));
  };

  const { createTracker, createTrackerLoading } = useCreateTracker({
    setTrackerGuid,
    setSeconds: (newSeconds) => {
      const newTime = subSeconds(new Date(), newSeconds);
      date.current = newTime;
      updateTime();
      setDelay(1000);
    },
  });

  useInterval(() => {
    updateTime();
  }, delay);

  const stop = () => {
    setDelay(null);
  };
  const start = () => {
    const startTime = new Date();
    createTracker(startTime);
    date.current = new Date();
    updateTime();
    setDelay(1000);
  };

  const resume = () => {
    const currentSeconds = secondsRef.current;
    date.current = subSeconds(new Date(), currentSeconds);
    updateTime();
    setDelay(1000);
  };

  const deleteTracker = () => {
    if (trackerGuid) {
      deleteActionTimeTracker(trackerGuid);
    }
  };

  return {
    time,
    secondsRef,
    start,
    resume,
    stop,
    createTrackerLoading,
    trackerGuid,
    deleteTracker,
  };
};
